import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface FlyOutsideClickHandlerProps {
  children: React.ReactNode;
  onOutsideClick: () => void;
  disabled?: boolean;
  /** @default 2147483647 — max z-index; lower if other portals (toasts, modals) must remain above */
  zIndex?: number;
}

/**
 * Renders a full-screen overlay (portaled to document.body) that blocks all
 * pointer interaction — clicks, hover effects, cursor changes — everywhere
 * except the children area. A clip-path with evenodd fill rule cuts a
 * rectangular hole over the children so they remain fully interactive.
 * The hole position stays in sync via ResizeObserver, scroll, and resize
 * listeners. Clicking anywhere on the overlay triggers `onOutsideClick`.
 *
 * **z-index caveat:** The overlay defaults to `2147483647` (max). Any
 * portal-based UI (toasts, modals, dialogs) with a lower z-index will be
 * blocked while this handler is active. Pass a custom `zIndex` if other
 * overlays must remain accessible.
 */
export function FlyOutsideClickHandler({
  children,
  onOutsideClick,
  disabled = false,
  zIndex = 2147483647,
}: FlyOutsideClickHandlerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [clipPath, setClipPath] = useState('inset(100%)');

  useLayoutEffect(() => {
    if (disabled || !containerRef.current) return;

    let rafId = 0;

    function update() {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const { top, left, bottom, right } = rect;
      setClipPath(
        `path(evenodd, "M0 0H10000V10000H0Z M${left} ${top}V${bottom}H${right}V${top}Z")`
      );
    }

    function scheduleUpdate() {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(update);
    }

    update();

    const observer = new ResizeObserver(scheduleUpdate);
    observer.observe(containerRef.current);
    window.addEventListener('scroll', scheduleUpdate, true);
    window.addEventListener('resize', scheduleUpdate);

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
      window.removeEventListener('scroll', scheduleUpdate, true);
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, [disabled]);

  if (disabled) return <>{children}</>;

  return (
    <>
      {createPortal(
        <div
          className='inset-0 fixed cursor-default'
          style={{
            zIndex,
            clipPath,
          }}
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onOutsideClick();
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        />,
        document.body
      )}
      <div ref={containerRef}>{children}</div>
    </>
  );
}
